<template>
  <panel :title="seminar.name">
    <template v-slot:actions>
      <el-dropdown trigger="click" placement="bottom" class="mr-10" @command="handlePrintCommand">
        <el-tooltip :content="$tc('general.print')" placement="top">
          <span>
            <el-button
              icon="el-icon-printer"
              size="medium"
              circle
            />
          </span>
        </el-tooltip>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="registration_fee"><i class="el-icon-tickets" />{{ $tc('general.registration_fee') }}</el-dropdown-item>
          <el-dropdown-item command="participants"><i class="el-icon-document" />{{ $tc('seminar.registered_participants') }}</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>

      <el-button
        v-if="user.isAdmin"
        size="medium"
        round
        class="mr-10"
        @click="$emit('print-documents')"
      >{{ $tc('tournament.print_documents') || 'Print Documents' }}</el-button>

      <el-dropdown v-if="user.isAdmin" trigger="click" @command="handleCommand">
        <el-button type="primary" icon="el-icon-more" circle />
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="edit"><i class="el-icon-edit" />{{ $tc('general.edit') }}</el-dropdown-item>
          <el-dropdown-item
            :command="seminar.registrationOpen ? 'close' : 'open'"
          >
            <i class="el-icon-edit" />{{ seminar.registrationOpen ? $tc('tournament.close_applications') : $tc('tournament.open_applications') }}
          </el-dropdown-item>
          <el-dropdown-item command="registration_fee"><i class="el-icon-tickets" />{{ $tc('general.registration_fee') }}</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </template>

    <div v-if="seminar.cover" class="cover">
      <img :src="$file('seminars', seminar.cover, seminar.id)">
    </div>

    <div v-if="seminar.images && seminar.images.length" class="gallery">
      <div v-for="image in seminar.images" :key="image.id" class="image">
        <img
          :src="$file('images', image.path)"
          @click="openPreviewImage(image.path)"
        >
        <div v-if="user.isAdmin" class="delete" @click="askDeleteImage(image)">
          <i class="el-icon-delete" />
        </div>
      </div>
    </div>

    <h4>{{ $tc('form.description') }}:</h4>
    <div class="description" v-html="seminar.description" />

    <template v-if="seminar.documents && seminar.documents.length">
      <h4>{{ $tc('general.documents') }}:</h4>
      <div class="documents">
        <file
          v-for="document in seminar.documents"
          :key="document.id"
          class="document"
          :file="document"
          :url="$file('documents', document.path, document.id)"
          :deletable="user.isAdmin"
          @delete="onDeleteDocument(document)"
        />
      </div>
    </template>

    <el-dialog :visible.sync="galleryVisible" width="90%">
      <img style="max-width: 100%" :src="$file('images', previewImage)">
    </el-dialog>
  </panel>
</template>

<script>
import errorHandler from '@/common/errorHandler.js'
import File from '@/components/files/File'
import Panel from '@/components/panel/Panel'

export default {
  components: {
    File,
    Panel
  },

  props: {
    seminar: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      previewImage: null,
      galleryVisible: false
    }
  },

  computed: {
    user () {
      return this.$store.state.auth.user
    }
  },

  watch: {
    galleryVisible (val) {
      if (!val) this.previewImage = null
    }
  },

  methods: {
    handleCommand (command) {
      if (command === 'edit') {
        this.$router.push({ name: 'EditSeminar', params: { id: this.seminar.id } })
      } else if (command === 'open' || command === 'close') {
        this.toggleRegistration(command === 'open')
      } else if (command === 'registration_fee') {
        this.$emit('registration-fee')
      }
    },

    async toggleRegistration (open) {
      try {
        await this.$store.dispatch('seminars/update', {
          id: this.seminar.id,
          payload: { registrationOpen: open }
        })
        this.$store.commit('seminars/setSingle', { ...this.seminar, registrationOpen: open })
        this.$message.success(open ? this.$tc('tournament.applications_opened') : this.$tc('tournament.applications_closed'))
      } catch (e) {
        errorHandler(e)
      }
    },

    handlePrintCommand (command) {
      if (command === 'registration_fee') {
        this.$emit('print-registration-fee')
      } else if (command === 'participants') {
        this.$emit('print-participants')
      }
    },

    openPreviewImage (path) {
      this.previewImage = path
      this.galleryVisible = true
    },

    askDeleteImage (image) {
      this.$confirm(this.$tc('general.confirm_delete', 0, { item: this.$tc('general.image') }))
        .then(() => this.deleteImage(image))
        .catch(() => {})
    },

    async deleteImage (image) {
      try {
        await this.$store.dispatch('seminarImages/delete', image.id)
        this.$store.commit('seminars/removeImage', { seminar: this.seminar, id: image.id })
        this.$message.success(this.$tc('general.deleted'))
      } catch (e) {
        errorHandler(e)
      }
    },

    onDeleteDocument (document) {
      this.$store.dispatch('seminarDocuments/delete', document.id).then(() => {
        this.$store.commit('seminars/removeDocument', { seminar: this.seminar, id: document.id })
        this.$message.success(this.$tc('general.deleted'))
      }).catch(errorHandler)
    }
  }
}
</script>

<style lang="scss" scoped>
.cover {
  margin-bottom: 20px;
  img {
    max-width: 100%;
    max-height: 300px;
    object-fit: contain;
  }
}
.gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
  .image {
    position: relative;
    img {
      width: 80px;
      height: 80px;
      object-fit: cover;
      cursor: pointer;
    }
    .delete {
      position: absolute;
      top: 0;
      right: 0;
      cursor: pointer;
      color: #f56c6c;
    }
  }
}
.description {
  margin-bottom: 20px;
}
.documents {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  .document {
    flex: 0 0 auto;
  }
}
</style>
